/**
 * ToolsSection — project detail "Tools" section (MVP-11 / MVP-11b).
 * See docs/phase2/mvp-architecture.md §3.6, §3.7.
 *
 * Renders the provisioning status of the five automated integrations (GitHub, Slack, Jira,
 * Clockify, SharePoint) from `project.provisioning_status`. Per tool:
 *   - linked       → tool name + external link (new tab) + green "Linked" badge;
 *   - pending      → "Pending" badge + spinner (queued, worker not started) + "Cancel auto-setup";
 *   - provisioning → "Provisioning…" badge + spinner (worker actively in flight) + "Cancel auto-setup";
 *   - failed       → orange "Auto-creation failed" badge + "Retry auto-setup" + "Enter link manually";
 *   - skipped      → grey "Skipped" badge + "Retry auto-setup" + "Enter link manually";
 *   - cancelled    → grey "Auto-setup cancelled" badge + "Retry auto-setup" + "Enter link manually".
 *
 * Cancel / Retry call POST /api/projects/{id}/integrations/{tool}/{action} (Provisioning API —
 * cancel asks for an inline confirmation first; a Jira retry accepts an optional replacement key,
 * availability-checked via GET /api/jira/validate-key). "Enter link manually" uses the existing
 * PATCH /api/projects/{id}, which flips the badge to "Linked". The header "Sync access" button
 * re-applies every assigned resource's access (POST /api/projects/{id}/resources/sync-access).
 *
 * Write controls are shown only for pm/sa (and admin/leadership); engineers see a read-only view.
 * The backend re-enforces the same RBAC on PATCH.
 *
 * "Swap" (linked tools only) re-points an ACTIVE integration at another existing resource via
 * POST …/integrations/{tool}/swap: the user enters the replacement, "Check" previews it (nothing is
 * written) and shows the current and the new resource side by side, then "Confirm swap" applies.
 * Shown to leadership/admin, and to the project's own PM; for GitHub/Slack the server only lets a PM
 * swap to a repo/channel they already belong to. The old resource is only unlinked.
 */

import { useState } from "react";
import { toast } from "sonner";
import {
  GitBranch,
  Hash,
  ListChecks,
  Clock,
  Globe,
  Loader2,
  ExternalLink,
  AlertTriangle,
  CheckCircle2,
  MinusCircle,
  Ban,
  RotateCw,
  Users,
  ArrowLeftRight,
  ArrowRight,
} from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import {
  useIntegrationAction,
  useIntegrationSwap,
  useSyncResourceAccess,
  useUpdateProject,
  useValidateJiraKey,
  type SyncResourceAccessResult,
} from "@/hooks/useProjects";
import {
  summariseSyncAccess,
  type SyncIntegration,
} from "@/lib/sync-access-summary";
import type {
  IntegrationSwapResponse,
  IntegrationSwapSide,
  Project,
  ProvisioningState,
  ProvisioningTool,
  UpdateProjectInput,
} from "@/types";
import { canSwapIntegration, sharePointLocationUrl } from "@/lib/linkage";
import { Badge, type BadgeVariant } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const WRITE_ROLES = ["pm", "sa", "leadership", "admin"];

/** Static, non-secret config per tool. */
interface ToolConfig {
  key: ProvisioningTool;
  label: string;
  Icon: typeof GitBranch;
  /** Resolve the display link href for a linked tool (or null when there is no external URL). */
  href: (p: Project) => string | null;
  /** Resolve the short link label shown next to the badge. */
  linkText: (p: Project) => string | null;
  /** The PATCH field the manual-fix form writes (matches the backend provisioning_status flip). */
  patchField: keyof UpdateProjectInput;
  inputLabel: string;
  inputPlaceholder: string;
  /** Label + placeholder of the swap input (accepts the same forms as "Migrate existing project"). */
  swapLabel: string;
  swapPlaceholder: string;
}

const TOOLS: ToolConfig[] = [
  {
    key: "github",
    label: "GitHub",
    Icon: GitBranch,
    href: (p) =>
      p.github_url ??
      (p.github_repo
        ? `https://github.com/Cloudelligent/${p.github_repo}`
        : null),
    linkText: (p) => p.github_repo ?? p.github_url ?? null,
    patchField: "github_repo",
    inputLabel: "GitHub repo name",
    inputPlaceholder: "e.g. dp-001",
    swapLabel: "New GitHub repository (name or URL)",
    swapPlaceholder: "e.g. acme-portal or https://github.com/CLDNT/acme-portal",
  },
  {
    key: "slack",
    label: "Slack",
    Icon: Hash,
    href: () => null,
    linkText: (p) => p.slack_micro_channel_id,
    patchField: "slack_micro_channel_id",
    inputLabel: "Slack channel id",
    inputPlaceholder: "e.g. C0123ABCD",
    swapLabel: "New Slack channel (id or #name)",
    swapPlaceholder: "e.g. #acme-delivery or C0123ABCD",
  },
  {
    key: "jira",
    label: "Jira",
    Icon: ListChecks,
    href: (p) => p.jira_project_url ?? null,
    linkText: (p) => p.jira_project_url ?? null,
    patchField: "jira_project_url",
    inputLabel: "Jira project URL",
    inputPlaceholder: "https://your-org.atlassian.net/browse/DP-001",
    swapLabel: "New Jira project (key or URL)",
    swapPlaceholder: "e.g. ACME or https://your-org.atlassian.net/browse/ACME",
  },
  {
    key: "clockify",
    label: "Clockify",
    Icon: Clock,
    href: (p) => p.clockify_project_url ?? null,
    linkText: (p) => p.clockify_project_url ?? p.clockify_project_id ?? null,
    patchField: "clockify_project_id",
    inputLabel: "Clockify project id",
    inputPlaceholder: "e.g. 64f0…",
    swapLabel: "New Clockify project (id or URL)",
    swapPlaceholder: "e.g. 64f0… or https://app.clockify.me/projects/…",
  },
  {
    key: "sharepoint",
    label: "SharePoint",
    Icon: Globe,
    // V059: a project may use a folder inside a shared site; link straight to that folder.
    href: (p) =>
      sharePointLocationUrl(p.sharepoint_site_url, p.sharepoint_folder_path),
    linkText: (p) =>
      p.sharepoint_folder_path
        ? (p.sharepoint_folder_path.split("/").pop() ?? null)
        : (p.sharepoint_site_url ?? null),
    patchField: "sharepoint_site_url",
    inputLabel: "SharePoint site or folder URL",
    inputPlaceholder:
      "https://cloudelligentcom.sharepoint.com/sites/project-name",
    swapLabel: "New SharePoint site or folder URL",
    swapPlaceholder:
      "https://cloudelligentcom.sharepoint.com/sites/PMOTeam/Shared%20Documents/General/…",
  },
];

/**
 * Badge descriptor. A named alias (not an inline object type) on purpose: it keeps the
 * `stateBadge` signature on ONE line, which `provisioning-state-mirror.test.ts` relies on to
 * isolate the function body — an inline multi-line return type ends its `\n}` match early.
 */
type StateBadge = {
  variant: BadgeVariant;
  label: string;
  Icon: typeof CheckCircle2;
};

/**
 * Map a provisioning state to a badge variant + label (text + colour, WCAG 2.1 AA).
 *
 * Every `ProvisioningState` needs an explicit arm. The `default` arm below is a last-resort guard,
 * NOT a place to let new states land: before this fix 'provisioning' fell through to `default` and
 * was mislabelled "Skipped" — i.e. the UI told the user the integration was switched off while a
 * worker was actively provisioning it. Guarded by
 * `src/types/__tests__/provisioning-state-mirror.test.ts`.
 */
function stateBadge(state: ProvisioningState): StateBadge {
  switch (state) {
    case "linked":
      return { variant: "success", label: "Linked", Icon: CheckCircle2 };
    case "pending":
      return { variant: "info", label: "Pending", Icon: Loader2 };
    case "provisioning":
      // Distinct label from 'pending' ("queued") so the two in-flight states are not conflated.
      return { variant: "info", label: "Provisioning…", Icon: Loader2 };
    case "failed":
      return {
        variant: "warning",
        label: "Auto-creation failed",
        Icon: AlertTriangle,
      };
    case "skipped":
      return { variant: "neutral", label: "Skipped", Icon: MinusCircle };
    case "cancelled":
      return { variant: "neutral", label: "Auto-setup cancelled", Icon: Ban };
    default:
      return { variant: "neutral", label: "Unknown", Icon: MinusCircle };
  }
}

/** States in which auto-setup is in flight → "Cancel auto-setup". */
const CANCELLABLE_STATES: ReadonlySet<ProvisioningState> = new Set([
  "pending",
  "provisioning",
]);
/** States from which auto-setup can be re-run → "Retry auto-setup" + "Enter link manually". */
const RETRYABLE_STATES: ReadonlySet<ProvisioningState> = new Set([
  "failed",
  "cancelled",
  "skipped",
]);

/** Resolve the effective state for a tool: explicit status, else infer from link presence. */
function resolveState(project: Project, tool: ToolConfig): ProvisioningState {
  const explicit = project.provisioning_status?.[tool.key]?.state;
  if (explicit) return explicit;
  return tool.linkText(project) ? "linked" : "pending";
}

/** Map an action error to a user-facing sentence (the server's 409 message is already specific). */
function actionErrorMessage(err: unknown, fallback: string): string {
  const data = (
    err as { response?: { data?: { code?: string; message?: string } } }
  )?.response?.data;
  if (data?.code === "INVALID_PROVISIONING_STATE") {
    return "The integration state changed in the meantime — refreshed. Please try again if needed.";
  }
  return data?.message ?? fallback;
}

type RowMode = "idle" | "manual" | "confirm-cancel" | "jira-retry" | "swap";

/** One side of the swap comparison ("Current" / "New"). */
function SwapSideView({
  heading,
  side,
}: {
  heading: string;
  side: IntegrationSwapSide;
}): JSX.Element {
  const label = side.display ?? side.value ?? "—";
  return (
    <div className="min-w-0 flex-1 rounded-md border border-outlines bg-background p-2">
      <p className="text-xs font-medium uppercase tracking-wide text-text-secondary">
        {heading}
      </p>
      {side.url ? (
        <a
          href={side.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex max-w-full items-center gap-1 break-all text-sm text-primary underline-offset-4 hover:underline"
        >
          {label}
          <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
        </a>
      ) : (
        <p className="break-all font-mono text-sm text-text-primary">{label}</p>
      )}
    </div>
  );
}

function ToolRow({
  project,
  tool,
  canManage,
  canSwap,
}: {
  project: Project;
  tool: ToolConfig;
  canManage: boolean;
  canSwap: boolean;
}): JSX.Element {
  const state = resolveState(project, tool);
  // Actions key off the EXPLICIT server marker only: an inferred 'pending' (legacy row with no
  // marker) has nothing to cancel and the server would answer 409.
  const explicitState = project.provisioning_status?.[tool.key]?.state;
  const badge = stateBadge(state);
  const href = tool.href(project);
  const linkText = tool.linkText(project);

  const [mode, setMode] = useState<RowMode>("idle");
  const [value, setValue] = useState("");
  const [jiraKey, setJiraKey] = useState("");
  const [rowError, setRowError] = useState<string | null>(null);
  const updateProject = useUpdateProject(project.jira_key);
  const integrationAction = useIntegrationAction(project.jira_key);
  const integrationSwap = useIntegrationSwap(project.jira_key);
  const [swapPreview, setSwapPreview] =
    useState<IntegrationSwapResponse | null>(null);
  const validateJiraKey = useValidateJiraKey();
  const [validating, setValidating] = useState(false);

  const canCancel =
    canManage &&
    explicitState !== undefined &&
    CANCELLABLE_STATES.has(explicitState);
  const canRetry = canManage && RETRYABLE_STATES.has(state);
  const busy = integrationAction.isPending || validating;

  const resetRow = (): void => {
    setMode("idle");
    setValue("");
    setJiraKey("");
    setRowError(null);
    setSwapPreview(null);
  };

  /** Step 1 — check the replacement and show both resources; nothing is written. */
  const handleSwapPreview = async (): Promise<void> => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setRowError(null);
    setSwapPreview(null);
    try {
      setSwapPreview(
        await integrationSwap.mutateAsync({
          tool: tool.key,
          value: trimmed,
          preview: true,
        }),
      );
    } catch (err) {
      setRowError(
        actionErrorMessage(err, `Could not check that ${tool.label} link.`),
      );
    }
  };

  /** Step 2 — apply exactly the value that was previewed. */
  const handleSwapConfirm = async (): Promise<void> => {
    if (!swapPreview) return;
    setRowError(null);
    try {
      const result = await integrationSwap.mutateAsync({
        tool: tool.key,
        value: value.trim(),
        preview: false,
      });
      toast.success(result.message || `${tool.label} swapped`);
      resetRow();
    } catch (err) {
      setRowError(actionErrorMessage(err, `Could not swap ${tool.label}.`));
    }
  };

  const handleSave = async (): Promise<void> => {
    const trimmed = value.trim();
    if (!trimmed) return;
    try {
      await updateProject.mutateAsync({
        [tool.patchField]: trimmed,
      } as UpdateProjectInput);
      toast.success(`${tool.label} linked`);
      resetRow();
    } catch {
      // The global axios interceptor already surfaces an error toast.
    }
  };

  const runAction = async (
    action: "cancel" | "retry",
    key?: string,
  ): Promise<void> => {
    setRowError(null);
    try {
      const result = await integrationAction.mutateAsync({
        tool: tool.key,
        action,
        ...(key ? { jiraProjectKey: key } : {}),
      });
      if (action === "cancel") {
        toast.success(`${tool.label} auto-setup cancelled`);
      } else if (result.state === "linked") {
        toast.success(`${tool.label} linked`);
      } else if (result.queued || result.state === "pending") {
        toast.success(`${tool.label} auto-setup restarted`);
      } else {
        toast.error(
          `${tool.label} auto-setup ${result.state === "skipped" ? "is not configured" : "failed again"}`,
        );
      }
      resetRow();
    } catch (err) {
      setRowError(
        actionErrorMessage(
          err,
          `Could not ${action} ${tool.label} auto-setup.`,
        ),
      );
    }
  };

  const handleJiraRetry = async (): Promise<void> => {
    const key = jiraKey.trim();
    if (!key) {
      // Blank → retry with the default key derivation.
      await runAction("retry");
      return;
    }
    setValidating(true);
    setRowError(null);
    const check = await validateJiraKey(key);
    setValidating(false);
    if (check && !check.available) {
      setRowError(
        check.message ??
          `Jira project key ${check.key} is already in use. Please choose a different key.`,
      );
      return;
    }
    await runAction("retry", check?.key ?? key);
  };

  const errorId = `tool-${tool.key}-error`;
  // T-14: the worker records WHY a tool failed (e.g. a read-only GitHub token). Only shown while
  // the tool is actually failed — a later success clears the reason from view.
  const failureReason =
    state === "failed"
      ? project.provisioning_status?.[tool.key]?.error_message
      : undefined;

  return (
    <li className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <tool.Icon
            className="h-4 w-4 shrink-0 text-text-secondary"
            aria-hidden="true"
          />
          <span className="font-medium text-text-primary">{tool.label}</span>
        </div>

        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          <Badge variant={badge.variant} className="gap-1">
            <badge.Icon
              className={`h-3 w-3 ${state === "pending" || state === "provisioning" ? "animate-spin" : ""}`}
              aria-hidden="true"
            />
            {badge.label}
          </Badge>

          {state === "linked" &&
            linkText &&
            (href ? (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex max-w-[12rem] items-center gap-1 truncate text-sm text-primary underline-offset-4 hover:underline"
              >
                <span className="truncate">{linkText}</span>
                <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
              </a>
            ) : (
              <span className="max-w-[12rem] truncate font-mono text-xs text-text-secondary">
                {linkText}
              </span>
            ))}

          {canSwap && state === "linked" && mode === "idle" && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setMode("swap")}
              aria-label={`Swap ${tool.label}`}
            >
              <ArrowLeftRight className="h-3.5 w-3.5" aria-hidden="true" />
              Swap
            </Button>
          )}

          {canCancel && mode === "idle" && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setMode("confirm-cancel")}
              aria-label={`Cancel ${tool.label} auto-setup`}
            >
              Cancel auto-setup
            </Button>
          )}

          {canRetry && mode === "idle" && (
            <>
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() =>
                  tool.key === "jira"
                    ? setMode("jira-retry")
                    : void runAction("retry")
                }
                aria-label={`Retry ${tool.label} auto-setup`}
              >
                {busy && (
                  <Loader2
                    className="h-4 w-4 animate-spin"
                    aria-hidden="true"
                  />
                )}
                <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
                Retry auto-setup
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setMode("manual")}
                aria-label={`Enter ${tool.label} link manually`}
              >
                Enter link manually
              </Button>
            </>
          )}
        </div>
      </div>

      {failureReason && (
        <p
          className="text-sm text-error"
          data-testid={`tool-${tool.key}-failure-reason`}
        >
          {failureReason}
        </p>
      )}

      {mode === "confirm-cancel" && canCancel && (
        <div
          role="group"
          aria-label={`Confirm cancelling ${tool.label} auto-setup`}
          className="flex flex-col gap-2 rounded-md border border-outlines bg-subtle-bg/30 p-3 sm:flex-row sm:items-center sm:justify-between"
        >
          <p className="text-sm text-text-primary">
            Stop automatic {tool.label} setup? You can retry it or enter a link
            manually later.
          </p>
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="ghost" onClick={resetRow}>
              Keep running
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline-danger"
              disabled={busy}
              onClick={() => void runAction("cancel")}
            >
              {busy && (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              )}
              Yes, cancel auto-setup
            </Button>
          </div>
        </div>
      )}

      {mode === "jira-retry" && canRetry && (
        <form
          className="flex flex-col gap-2 rounded-md border border-outlines bg-subtle-bg/30 p-3 sm:flex-row sm:items-end"
          onSubmit={(e) => {
            e.preventDefault();
            void handleJiraRetry();
          }}
        >
          <div className="flex-1 space-y-1.5">
            <Label htmlFor={`retry-${tool.key}-key`} className="text-xs">
              Jira project key (optional — leave blank to reuse the default)
            </Label>
            <Input
              id={`retry-${tool.key}-key`}
              value={jiraKey}
              onChange={(e) => {
                setJiraKey(e.target.value);
                if (rowError) setRowError(null);
              }}
              placeholder="e.g. CST667"
              autoComplete="off"
              aria-invalid={rowError ? true : undefined}
              aria-describedby={rowError ? errorId : undefined}
            />
          </div>
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="ghost" onClick={resetRow}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={busy}>
              {busy && (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              )}
              Retry
            </Button>
          </div>
        </form>
      )}

      {mode === "manual" && canRetry && (
        <form
          className="flex flex-col gap-2 rounded-md border border-outlines bg-subtle-bg/30 p-3 sm:flex-row sm:items-end"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSave();
          }}
        >
          <div className="flex-1 space-y-1.5">
            <Label htmlFor={`fix-${tool.key}`} className="text-xs">
              {tool.inputLabel}
            </Label>
            <Input
              id={`fix-${tool.key}`}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={tool.inputPlaceholder}
              autoComplete="off"
            />
          </div>
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="ghost" onClick={resetRow}>
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={updateProject.isPending || !value.trim()}
            >
              {updateProject.isPending && (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              )}
              Save
            </Button>
          </div>
        </form>
      )}

      {mode === "swap" && canSwap && (
        <div
          role="group"
          aria-label={`Swap ${tool.label}`}
          className="flex flex-col gap-3 rounded-md border border-outlines bg-subtle-bg/30 p-3"
        >
          <form
            className="flex flex-col gap-2 sm:flex-row sm:items-end"
            onSubmit={(e) => {
              e.preventDefault();
              void handleSwapPreview();
            }}
          >
            <div className="flex-1 space-y-1.5">
              <Label htmlFor={`swap-${tool.key}`} className="text-xs">
                {tool.swapLabel}
              </Label>
              <Input
                id={`swap-${tool.key}`}
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  // A changed value must be checked again before it can be applied.
                  setSwapPreview(null);
                  if (rowError) setRowError(null);
                }}
                placeholder={tool.swapPlaceholder}
                autoComplete="off"
                aria-invalid={rowError ? true : undefined}
                aria-describedby={rowError ? errorId : undefined}
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={resetRow}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                variant="outline"
                disabled={integrationSwap.isPending || !value.trim()}
              >
                {integrationSwap.isPending && !swapPreview && (
                  <Loader2
                    className="h-4 w-4 animate-spin"
                    aria-hidden="true"
                  />
                )}
                Check
              </Button>
            </div>
          </form>

          {swapPreview && (
            <div
              className="flex flex-col gap-2"
              data-testid={`swap-${tool.key}-preview`}
            >
              <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
                <SwapSideView heading="Current" side={swapPreview.before} />
                <ArrowRight
                  className="mx-auto h-4 w-4 shrink-0 rotate-90 text-text-secondary sm:rotate-0"
                  aria-hidden="true"
                />
                <SwapSideView heading="New" side={swapPreview.after} />
              </div>
              <p className="text-sm text-text-secondary" role="status">
                {swapPreview.message} The current {tool.label} link will only be
                unlinked from this project — nobody is removed from it and
                nothing is deleted.
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={resetRow}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  disabled={integrationSwap.isPending}
                  onClick={() => void handleSwapConfirm()}
                >
                  {integrationSwap.isPending && (
                    <Loader2
                      className="h-4 w-4 animate-spin"
                      aria-hidden="true"
                    />
                  )}
                  Confirm swap
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {rowError && (
        <p
          id={errorId}
          role="alert"
          className="flex items-center gap-1.5 text-sm text-error"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
          {rowError}
        </p>
      )}
    </li>
  );
}

export interface ToolsSectionProps {
  project: Project;
}

export function ToolsSection({ project }: ToolsSectionProps): JSX.Element {
  const { user } = useAuth();
  const canManage = WRITE_ROLES.includes(user?.role ?? "");
  const canSwap = canSwapIntegration(user, project.project_manager);
  const syncAccess = useSyncResourceAccess(project.jira_key);

  const handleSync = async (): Promise<void> => {
    let result: SyncResourceAccessResult;
    try {
      result = await syncAccess.mutateAsync();
    } catch {
      // The global axios interceptor already surfaces an error toast.
      return;
    }
    // The server answers 200 even when every invite failed — the counts are the truth.
    const linked = Object.fromEntries(
      TOOLS.map((tool) => [tool.key, resolveState(project, tool) === "linked"]),
    ) as Partial<Record<SyncIntegration, boolean>>;
    const summary = summariseSyncAccess({
      counts: result?.counts,
      resourceCount: result?.resource_count,
      linked,
    });
    if (summary.kind === "success") toast.success(summary.message);
    else if (summary.kind === "warning") toast.warning(summary.message);
    else toast.info(summary.message);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <h3 className="flex items-center gap-2 text-base font-semibold text-text-primary">
          <GitBranch className="h-4 w-4 text-primary" aria-hidden="true" />
          Tools
        </h3>
        {canManage && (
          <Button
            size="sm"
            variant="outline"
            disabled={syncAccess.isPending}
            onClick={() => void handleSync()}
            aria-label="Sync team access to all linked tools"
          >
            {syncAccess.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Users className="h-4 w-4" aria-hidden="true" />
            )}
            Sync access
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <ul className="divide-y divide-outlines">
          {TOOLS.map((tool) => (
            <ToolRow
              key={tool.key}
              project={project}
              tool={tool}
              canManage={canManage}
              canSwap={canSwap}
            />
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

export default ToolsSection;
